import { motion } from 'framer-motion';
import { Settings, Gauge, Cpu, Zap } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { CTASection } from '@/components/sections/CTASection';
import { getBreadcrumbJsonLd, getFaqJsonLd, BASE_URL } from '@/lib/seo-constants';
import { useT, useLang } from '@/lib/i18n';
import fraeseVmx30 from '@/assets/maschinenpark-fraese-vmx30.webp';
import fraeseVmx42 from '@/assets/maschinenpark-fraese-vmx42.webp';
import dreheEmco from '@/assets/maschinenpark-drehe-emco.webp';
import gdwZ280 from '@/assets/maschinenpark-gdw.webp';
import gdwLz400 from '@/assets/maschinenpark-gdw-lz400.webp';
import weilerCondor from '@/assets/maschinenpark-weiler.webp';
import konventionell from '@/assets/maschinenpark-konventionell.webp';
import trumpfLaser from '@/assets/maschinenpark-trumpf-laser.webp';

import { MachineCategory } from '@/components/sections/MachineCategory';
import { MachineStats } from '@/components/sections/MachineStats';
import { FaqSection } from '@/components/sections/FaqSection';

export interface MachineSpec {
  label: string;
  value: string;
}

export interface Machine {
  name: string;
  quantity: number;
  image: string;
  specs: MachineSpec[];
  description: string;
}

export interface MachineCategoryData {
  category: string;
  icon: React.ElementType;
  items: Machine[];
}

export default function MaschinenparkPage() {
  const t = useT();
  const lang = useLang();
  const yearL = t('Baujahr', 'Year of build');
  const yearsL = t('Baujahre', 'Years of build');
  const axesL = t('Achsen', 'Axes');
  const travelXYZ = t('Verfahrweg X-Y-Z', 'Travel X-Y-Z');
  const travelXZ = t('Verfahrweg X-Z', 'Travel X-Z');
  const tableL = t('Tisch', 'Table');
  const controlL = t('Steuerung', 'Control');
  const spindleSpeed = t('Spindeldrehzahl', 'Spindle speed');
  const toolChanger = t('Werkzeugwechsler', 'Tool changer');
  const tools = t('Werkzeuge', 'Tools');
  const barLoader = t('Stangenlader', 'Bar feeder');
  const maxDia = t('Max. Drehdurchmesser', 'Max. turning diameter');
  const maxLen = t('Max. Drehlänge', 'Max. turning length');
  const spindleBore = t('Spindelbohrung', 'Spindle bore');
  const chuck = t('Futter', 'Chuck');
  const laserType = t('Lasertyp', 'Laser type');
  const laserPower = t('Laserleistung', 'Laser power');
  const workArea = t('Arbeitsbereich', 'Work area');
  const wavelength = t('Wellenlänge', 'Wavelength');
  const rpm = t('U/min', 'rpm');
  const slots = t('Plätze', 'slots');
  const driven = t('angetrieben', 'driven');
  const withRotary = t('mit Rundtisch', 'with rotary table');
  const yAxisFrom = t('Y-Achse ab 2017', 'Y-axis from 2017');
  const upToDia = t('bis Ø 65 mm', 'up to Ø 65 mm');
  const cAxis = t('2 (+ C-Achse)', '2 (+ C-axis)');
  const machines: MachineCategoryData[] = [
    {
      category: t('CNC-Fräszentren', 'CNC milling centres'),
      icon: Settings,
      items: [
        { name: 'HURCO VMX 30t', quantity: 1, image: fraeseVmx30, specs: [
          { label: yearL, value: '2008' }, { label: axesL, value: `5 (${withRotary})` }, { label: travelXYZ, value: '760-510-610 mm' },
          { label: tableL, value: '1020 × 510 mm' }, { label: controlL, value: 'HURCO Ultimax 4' },
          { label: spindleSpeed, value: `10.000 ${rpm}` }, { label: toolChanger, value: `24 ${slots} (SK 40)` },
        ], description: t('5-Achs-Fräszentrum mit Rundtisch für komplexe Geometrien.', '5-axis milling centre with rotary table for complex geometries.') },
        { name: 'HURCO VMX 30i', quantity: 1, image: fraeseVmx30, specs: [
          { label: yearL, value: '2015' }, { label: axesL, value: `5 (${withRotary})` }, { label: travelXYZ, value: '762-508-508 mm' },
          { label: tableL, value: '1020 × 510 mm' }, { label: controlL, value: 'HURCO WinMax' },
          { label: spindleSpeed, value: `12.000 ${rpm}` }, { label: toolChanger, value: `40 ${slots} (SK 40)` },
        ], description: t('5-Achs-Bearbeitungszentrum mit HURCO WinMax Steuerung.', '5-axis machining centre with HURCO WinMax control.') },
        { name: 'HURCO VMX 42t', quantity: 1, image: fraeseVmx42, specs: [
          { label: yearL, value: '2012' }, { label: axesL, value: '3' }, { label: travelXYZ, value: '1067-610-610 mm' },
          { label: tableL, value: '1270 × 610 mm' }, { label: controlL, value: 'HURCO WinMax' },
          { label: spindleSpeed, value: `12.000 ${rpm}` }, { label: toolChanger, value: `40 ${slots} (SK 40)` },
        ], description: t('Großes 3-Achs-Bearbeitungszentrum für Werkstücke bis über 1 Meter.', 'Large 3-axis machining centre for workpieces over 1 metre.') },
      ],
    },
    {
      category: t('CNC-Drehzentren', 'CNC turning centres'),
      icon: Gauge,
      items: [
        { name: 'EMCO TURN E65', quantity: 3, image: dreheEmco, specs: [
          { label: yearsL, value: '2010, 2017, 2019' }, { label: axesL, value: `3–4 (${yAxisFrom})` },
          { label: barLoader, value: upToDia }, { label: travelXYZ, value: '260-80-610 mm' },
          { label: controlL, value: 'Siemens Sinumerik 828D' }, { label: spindleSpeed, value: `5.000 ${rpm}` },
          { label: tools, value: `12 ${slots}, ${driven}` },
        ], description: t('CNC-Drehmaschinen mit angetriebenen Werkzeugen und Stangenlader für Komplettbearbeitung.', 'CNC lathes with driven tools and bar feeder for full machining in one setup.') },
        { name: 'EMCO TURN E25', quantity: 1, image: dreheEmco, specs: [
          { label: yearL, value: '2022' }, { label: axesL, value: cAxis }, { label: travelXZ, value: '100-300 mm' },
          { label: controlL, value: 'Siemens Sinumerik 828D' }, { label: spindleSpeed, value: `6.300 ${rpm}` },
          { label: tools, value: `6 ${slots}, ${driven}` },
        ], description: t('Kompakte CNC-Drehmaschine mit C-Achse für kleine Präzisionsteile.', 'Compact CNC lathe with C-axis for small precision parts.') },
      ],
    },
    {
      category: t('Konventionelle Drehmaschinen', 'Conventional lathes'),
      icon: Cpu,
      items: [
        { name: 'GDW LZ 400 VS1 Comfortline', quantity: 1, image: gdwLz400, specs: [
          { label: maxDia, value: 'Ø 400 mm' }, { label: maxLen, value: '1.000 mm' },
          { label: spindleSpeed, value: `30–4.000 ${rpm}` }, { label: spindleBore, value: 'Ø 62 mm' },
          { label: chuck, value: 'Ø 200 mm' },
        ], description: t('Konventionelle Drehmaschine mit Backenfutter für flexible Einzelfertigung.', 'Conventional lathe with jaw chuck for flexible one-off production.') },
        { name: 'GDW Z 280 VS Comfortline', quantity: 1, image: gdwZ280, specs: [
          { label: maxDia, value: 'Ø 330 mm' }, { label: maxLen, value: '670 mm' },
          { label: spindleSpeed, value: `60–3.000 ${rpm}` }, { label: spindleBore, value: 'Ø 43 mm' },
          { label: chuck, value: 'Ø 160 mm' },
        ], description: t('Konventionelle Drehmaschine mit Spannzange für mittlere Durchmesser.', 'Conventional lathe with collet chuck for medium diameters.') },
        { name: 'Weiler Condor VC', quantity: 1, image: weilerCondor, specs: [
          { label: maxDia, value: 'Ø 200 mm' }, { label: maxLen, value: '500 mm' },
        ], description: t('Robuste konventionelle Drehmaschine für größere Durchmesser und Sonderanfertigungen.', 'Robust conventional lathe for larger diameters and custom work.') },
      ],
    },
    {
      category: t('Lasertechnik', 'Laser technology'),
      icon: Zap,
      items: [
        { name: 'TRUMPF Lasersign PL2', quantity: 1, image: trumpfLaser, specs: [
          { label: laserType, value: 'Nd:YVO4' }, { label: laserPower, value: '120 W' },
          { label: workArea, value: '350 × 320 × 236 mm' }, { label: wavelength, value: '1064 nm' },
        ], description: t('Lasergerät zur Beschriftung und Gravur von Metallen und Kunststoffen.', 'Laser system for marking and engraving on metals and plastics.') },
      ],
    },
  ];
  const stats = [
    { value: '11', label: t('Maschinen gesamt', 'Total machines') },
    { value: '5', label: t('Achsen max.', 'Max. axes') },
    { value: '1067 mm', label: t('Max. Verfahrweg', 'Max. travel') },
    { value: 'Ø 200 mm', label: maxDia },
  ];
  const faqs = [
    { question: t('Welche CNC-Fräsmaschinen hat FERNAU?', 'Which CNC milling machines does FERNAU operate?'), answer: t('FERNAU betreibt drei HURCO Bearbeitungszentren: VMX 30t (5-Achs, Bj. 2008), VMX 30i (5-Achs, Bj. 2015) und VMX 42t (3-Achs, Bj. 2012) mit Verfahrwegen bis 1.067 mm.', 'FERNAU operates three HURCO machining centres: VMX 30t (5-axis, 2008), VMX 30i (5-axis, 2015) and VMX 42t (3-axis, 2012) with travels up to 1,067 mm.') },
    { question: t('Welche CNC-Drehmaschinen hat FERNAU?', 'Which CNC lathes does FERNAU operate?'), answer: t('FERNAU setzt vier EMCO Drehmaschinen ein: 3× EMCO TURN E65 (Bj. 2010, 2017, 2019) mit Y-Achse und Stangenlader bis Ø 65 mm sowie 1× EMCO TURN E25 (Bj. 2022) mit C-Achse.', 'FERNAU uses four EMCO lathes: 3× EMCO TURN E65 (2010, 2017, 2019) with Y-axis and bar feeder up to Ø 65 mm, plus 1× EMCO TURN E25 (2022) with C-axis.') },
    { question: t('Verfügt FERNAU über Lasertechnik?', 'Does FERNAU have laser technology?'), answer: t('Ja, FERNAU besitzt einen TRUMPF Lasersign PL2 (Nd:YVO4, 120 W) für Laserbeschriftung und Gravur auf Metallen und Kunststoffen.', 'Yes, FERNAU operates a TRUMPF Lasersign PL2 (Nd:YVO4, 120 W) for laser marking and engraving on metals and plastics.') },
    { question: t('Welche konventionellen Drehmaschinen sind vorhanden?', 'Which conventional lathes are available?'), answer: t('FERNAU verfügt über drei konventionelle Drehmaschinen: GDW LZ 400 VS1 Comfortline (Ø 400 mm), GDW Z 280 VS Comfortline (Ø 330 mm) und eine Weiler Condor VC (Ø 200 mm) für flexible Einzelfertigung und Sonderanfertigungen.', 'FERNAU has three conventional lathes: GDW LZ 400 VS1 Comfortline (Ø 400 mm), GDW Z 280 VS Comfortline (Ø 330 mm) and a Weiler Condor VC (Ø 200 mm) for flexible one-off and custom production.') },
    { question: t('Wie viele Maschinen hat FERNAU insgesamt?', 'How many machines does FERNAU operate in total?'), answer: t('Der Maschinenpark umfasst 11 Maschinen: 3 CNC-Fräszentren, 4 CNC-Drehmaschinen, 3 konventionelle Drehmaschinen und 1 Laseranlage.', 'The machine park consists of 11 machines: 3 CNC milling centres, 4 CNC lathes, 3 conventional lathes and 1 laser system.') },
  ];
  return (
    <Layout>
      <SEOHead
        title={t('Maschinenpark – CNC-Fräs- & Drehzentren', 'Machine Park – CNC Milling & Turning Centres')}
        description={t('Unser Maschinenpark: HURCO VMX 30t, VMX 30i & VMX 42t Fräszentren, EMCO TURN E65 & E25 Drehmaschinen, TRUMPF Laser. Modernste CNC-Technik in Darmstadt.', 'Our machine park: HURCO VMX 30t, VMX 30i & VMX 42t milling centres, EMCO TURN E65 & E25 lathes, TRUMPF laser. State-of-the-art CNC technology in Darmstadt, Germany.')}
        path="/maschinenpark"
        jsonLd={[
          getBreadcrumbJsonLd([{ name: t('Maschinenpark', 'Machine park'), path: '/maschinenpark' }], lang),
          getFaqJsonLd(faqs, lang),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: t('Maschinenpark FERNAU Präzisionstechnik', 'Machine park FERNAU Präzisionstechnik'),
            numberOfItems: machines.reduce((sum, c) => sum + c.items.length, 0),
            itemListElement: machines.flatMap((category, ci) =>
              category.items.map((m, mi) => ({
                '@type': 'ListItem',
                position: ci * 100 + mi + 1,
                item: {
                  '@type': 'Thing',
                  name: m.name,
                  description: m.description,
                  image: `${BASE_URL}${typeof m.image === 'string' ? m.image : ''}`,
                  additionalProperty: m.specs.map((s) => ({
                    '@type': 'PropertyValue',
                    name: s.label,
                    value: s.value,
                  })),
                },
              })),
            ),
          },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.span
            className="text-primary font-mono text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {t('Technologie', 'Technology')}
          </motion.span>
          <motion.h1
            className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t(<>Unser <span className="text-primary">Maschinenpark</span></>, <>Our <span className="text-primary">Machine Park</span></>)}
          </motion.h1>
          <motion.p
            className="mt-6 text-xl text-muted-foreground max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Modernste CNC-Technologie von HURCO und EMCO, ergänzt durch konventionelle Drehmaschinen und TRUMPF Lasertechnik – für maximale Präzision und Flexibilität.',
              'State-of-the-art CNC technology from HURCO and EMCO, complemented by conventional lathes and TRUMPF laser technology – for maximum precision and flexibility.'
            )}
          </motion.p>
        </div>
      </section>

      {/* Machine Categories */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="space-y-24">
            {machines.map((category, categoryIndex) => (
              <MachineCategory key={category.category} category={category} index={categoryIndex} />
            ))}
          </div>
        </div>
      </section>

      {/* Summary Stats */}
      <MachineStats stats={stats} />

      <FaqSection
        faqs={faqs}
      />

      <CTASection />
    </Layout>
  );
}
